import {
  ActionIcon,
  Box,
  Button,
  Checkbox,
  Group,
  Paper,
  Radio,
  Stack,
  Text,
  TextInput,
  ThemeIcon,
  Title,
  Modal,
  Skeleton,
} from "@mantine/core";
import {
  IconListCheck,
  IconPlus,
  IconQuestionMark,
  IconTrash,
  IconEdit,
} from "@tabler/icons-react";
import { motion } from "framer-motion";
import { useState, useRef, useEffect } from "react";
import { useDisclosure } from "@mantine/hooks";

import { Container } from "@mui/material";
import { Link, useParams } from "react-router-dom";
import { Colors } from "../../Lib/data";
import { Endpoints } from "../../Lib/endpoints";
import { NonPaginatedResponse } from "../../Lib/responses";
import {
  AssessmentProps,
  QuestionOptionProps,
  QuestionProps,
} from "../../Lib/types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";
import "./styles.scss";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  getEllipsisFromString,
} from "../../Lib/methods";
import { doesNotMatch } from "assert";

interface Option {
  id: string;
  title: string;
  isCorrect: boolean;
}

export default function SingleAssessment() {
  const { assessmentID } = useParams();
  const [discardOpened, { open: openDiscard, close: closeDiscard }] =
    useDisclosure(false);
  const [deleteOpened, { open: openDelete, close: closeDelete }] =
    useDisclosure(false);
  const [questionToDelete, setQuestionToDelete] = useState<string | null>(null);

  const {
    data: assessment,
    isLoading: isLoadingAssessments,
    reloadData: reloadAssessments,
  } = usePerformRequest<NonPaginatedResponse<AssessmentProps>>({
    method: Endpoints.GetSingleAssessment(assessmentID as string).method,
    route: Endpoints.GetSingleAssessment(assessmentID as string).route,
  });

  console.log(assessment);

  const [questions, setQuestions] = useState<QuestionProps[]>([]);

  useEffect(() => {
    if (
      assessment &&
      assessment.data &&
      assessment.data.questions &&
      assessment.data.questions.length > 0
    ) {
      setQuestions(assessment.data.questions);
    }
  }, [assessment]);

  const [currentQuestion, setCurrentQuestion] = useState<QuestionProps>({
    answer: null,
    id: "",
    title: "",
    options: [],
    type: "single",
  });
  const [isEditing, setIsEditing] = useState(false);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [focusedOptionId, setFocusedOptionId] = useState<string | null>(null);
  const optionRefs = useRef<{ [key: string]: HTMLInputElement | null }>({});
  const questionTitleRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState<string | null>(null);

  const handleTypeChange = (type: "single" | "multiple") => {
    if (type === "single") {
      setCurrentQuestion((prev) => ({
        ...prev,
        type,
        answer: prev.answer ? [prev.answer[0]] : null,
      }));
    } else {
      setCurrentQuestion((prev) => ({
        ...prev,
        type,
        answer: prev.answer || null,
      }));
    }
  };

  const addOption = () => {
    const newOptionId = Date.now().toString();
    setCurrentQuestion((prev) => ({
      ...prev,
      options: [
        ...prev.options,
        {
          value: newOptionId,
          label: "",
        },
      ],
    }));
    setFocusedOptionId(newOptionId);
  };

  const handleOptionKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>,
    optionId: string
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      addOption();
    }

    // Handle Ctrl+Shift+A or Command+Shift+A
    if ((e.ctrlKey || e.metaKey) && e.shiftKey && e.key.toLowerCase() === "a") {
      e.preventDefault();
      setCurrentQuestion((prev) => {
        if (prev.type === "single") {
          return {
            ...prev,
            answer: [optionId],
          };
        } else {
          const currentAnswers = prev.answer || [];
          const isAlreadyAnswer = currentAnswers.includes(optionId);
          return {
            ...prev,
            answer: isAlreadyAnswer
              ? currentAnswers.filter((id) => id !== optionId)
              : [...currentAnswers, optionId],
          };
        }
      });
    }

    // Handle Tab navigation
    if (e.key === "Tab" && !e.shiftKey) {
      const currentIndex = currentQuestion.options.findIndex(
        (opt) => opt.value === optionId
      );
      if (currentIndex < currentQuestion.options.length - 1) {
        e.preventDefault();
        const nextOptionId = currentQuestion.options[currentIndex + 1].value;
        setFocusedOptionId(nextOptionId);
        // Use setTimeout to ensure the focus happens after the state update
        setTimeout(() => {
          optionRefs.current[nextOptionId]?.focus();
        }, 0);
      }
    }
  };

  // Effect to handle focus when focusedOptionId changes
  useEffect(() => {
    if (focusedOptionId) {
      optionRefs.current[focusedOptionId]?.focus();
    }
  }, [focusedOptionId]);

  const removeOption = (optionId: string) => {
    setCurrentQuestion((prev) => ({
      ...prev,
      options: prev.options.filter((opt) => opt.value !== optionId),
    }));
  };

  const updateOption = (
    optionId: string,
    field: keyof QuestionOptionProps,
    value: string | boolean
  ) => {
    setCurrentQuestion((prev) => ({
      ...prev,
      options: prev.options.map((opt) =>
        opt.value === optionId ? { ...opt, [field]: value } : opt
      ),
    }));
  };

  const createNewQuestion = () => {
    setCurrentQuestion({
      answer: null,
      id: "",
      title: "",
      options: [],
      type: "single",
    });
  };

  const isQuestionValid = (): boolean => {
    // Check if title is empty
    if (!currentQuestion.title.trim()) {
      return false;
    }

    // Check if there are at least two options
    if (currentQuestion.options.length < 2) {
      return false;
    }

    // Check if any option is empty
    const hasEmptyOption = currentQuestion.options.some(
      (opt) => !opt.label || opt.label.trim() === ""
    );
    if (hasEmptyOption) {
      return false;
    }

    // Check if an answer is selected
    if (currentQuestion.type === "single" && !currentQuestion.answer) {
      return false;
    }
    if (
      currentQuestion.type === "multiple" &&
      (!currentQuestion.answer ||
        (Array.isArray(currentQuestion.answer) &&
          currentQuestion.answer.length === 0))
    ) {
      return false;
    }

    return true;
  };

  const validateQuestion = (): boolean => {
    if (!isQuestionValid()) {
      if (!currentQuestion.title.trim()) {
        setError("Question title cannot be empty");
      } else if (currentQuestion.options.length < 2) {
        setError("Question must have at least two options");
      } else if (
        currentQuestion.options.some(
          (opt) => !opt.label || opt.label.trim() === ""
        )
      ) {
        setError("All options must have text");
      } else if (currentQuestion.type === "single" && !currentQuestion.answer) {
        setError("Please select a correct answer");
      } else if (
        currentQuestion.type === "multiple" &&
        (!currentQuestion.answer ||
          (Array.isArray(currentQuestion.answer) &&
            currentQuestion.answer.length === 0))
      ) {
        setError("Please select at least one correct answer");
      }
      return false;
    }

    setError(null);
    return true;
  };

  const [isSaving, setIsSaving] = useState(false);
  const SendNewQuestionToServer = async () => {
    setIsSaving(true);
    const r = await PerformRequest({
      method: Endpoints.GetAddAssessmentQuestion(assessmentID as string).method,
      route: Endpoints.GetAddAssessmentQuestion(assessmentID as string).route,
      data: currentQuestion,
      callback: () => {
        setIsSaving(false);
      },
    });
    setIsSaving(false);
    if (r) {
      if (r.status === 200) {
        setQuestions((prev) => [...prev, currentQuestion]);
        createNewQuestion();
        DefaultSuccessNotification(
          "Question Added",
          "You can now add another question"
        );
      } else {
        DefaultErrorNotification(
          "Failed to add question",
          "Please try again later"
        );
      }
    }
  };
  const SendUpdatedQuestionToServer = async (index: number) => {
    setIsSaving(true);
    const r = await PerformRequest({
      method: Endpoints.GetUpdateAssessmentQuestion(
        assessmentID as string,
        currentQuestion.id
      ).method,
      route: Endpoints.GetUpdateAssessmentQuestion(
        assessmentID as string,
        currentQuestion.id
      ).route,
      data: currentQuestion,
      callback: () => {
        setIsSaving(false);
      },
    });
    setIsSaving(false);
    if (r) {
      if (r.status === 200) {
        setQuestions((prev) => {
          const newQuestions = [...prev];
          newQuestions[index] = currentQuestion;
          return newQuestions;
        });
        setIsEditing(false);
        setEditingIndex(null);
        createNewQuestion();
        DefaultSuccessNotification(
          "Question Updated",
          "You can now add another question"
        );
      } else {
        DefaultErrorNotification(
          "Failed to update question",
          "Please try again later"
        );
      }
    }
  };
  const saveQuestion = () => {
    if (!validateQuestion()) {
      return;
    }

    if (isEditing && editingIndex !== null) {
      SendUpdatedQuestionToServer(editingIndex);
    } else {
      SendNewQuestionToServer();
    }
    createNewQuestion();
  };

  const editQuestion = (index: number) => {
    setCurrentQuestion(questions[index]);
    console.log(questions[index]);
    setIsEditing(true);
    setEditingIndex(index);
  };

  const discardCurrentQuestion = () => {
    if (
      currentQuestion.title.trim() === "" &&
      currentQuestion.options.length === 0
    ) {
      return;
    }

    openDiscard();
  };

  const handleDiscardConfirm = () => {
    createNewQuestion();
    setIsEditing(false);
    setEditingIndex(null);
    closeDiscard();
  };

  const focusFirstOption = () => {
    if (currentQuestion.options.length === 0) {
      addOption();
    } else {
      setFocusedOptionId(currentQuestion.options[0].value);
    }
  };

  const handleQuestionTitleKeyPress = (
    e: React.KeyboardEvent<HTMLInputElement>
  ) => {
    if (e.key === "Enter") {
      e.preventDefault();
      focusFirstOption();
    }
  };

  const handleDeleteQuestion = (questionID: string) => {
    setQuestionToDelete(questionID);
    openDelete();
  };

  const [isDeletingQuestion, setIsDeletingQuestion] = useState(false);
  const ConfirmDeleteQuestion = async () => {
    console.log("Before delete", questionToDelete);
    if (questionToDelete) {
      console.log("After delete", questionToDelete);
      setIsDeletingQuestion(true);
      const r = await PerformRequest({
        method: Endpoints.GetDeleteAssessmentQuestion(
          assessmentID as string,
          questionToDelete
        ).method,
        route: Endpoints.GetDeleteAssessmentQuestion(
          assessmentID as string,
          questionToDelete
        ).route,
        callback: () => {
          setIsDeletingQuestion(false);
        },
      });
      setIsDeletingQuestion(false);
      if (r) {
        if (r.status === 200) {
          setQuestions((prev) => prev.filter((q) => q.id !== questionToDelete));
          setQuestionToDelete(null);
          DefaultSuccessNotification(
            "Question Deleted",
            "The question has been deleted"
          );
        } else {
          DefaultErrorNotification(
            "Failed to delete question",
            "Please try again later"
          );
        }
      } else {
        DefaultErrorNotification(
          "Failed to delete question",
          "Please try again later"
        );
      }
    }
    closeDelete();
  };

  return (
    <Container maxWidth="lg">
      <motion.div
        className="single-assessment-container flex-col width-100"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Box p="xl" style={{ maxWidth: "1200px", margin: "0 auto" }}>
          <motion.div
            initial={{ y: -20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5 }}
          >
            <Group mb="xl" align="center">
              <ThemeIcon size={50} radius={50} color="purple">
                <IconQuestionMark size={30} />
              </ThemeIcon>
              <div>
                {isLoadingAssessments ? (
                  <Skeleton height={24} width={500} />
                ) : (
                  <Text fw={500} fz={24} c={Colors.PurplePrimary}>
                    {assessment?.data.title}
                  </Text>
                )}
                <Text c="dimmed" size="sm">
                  Create and manage your assessment questions
                </Text>
                <Link to={`/d/lesson/assessments/${assessment?.data.lessonID}`}>
                  <Button
                    size="compact-xs"
                    variant="subtle"
                    color={Colors.PurplePrimary}
                  >
                    Back to Assessments
                  </Button>
                </Link>
              </div>
            </Group>
          </motion.div>

          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <Paper
              p="xl"
              shadow="md"
              mb="xl"
              w={600}
              style={{
                background:
                  "linear-gradient(to bottom right, var(--mantine-color-purple-0), white)",
                border: `1px solid var(--mantine-color-gray-3)`,
              }}
            >
              <Stack>
                <Group justify="space-between" mb="md">
                  <Group>
                    <ThemeIcon size={40} radius={50} color="purple">
                      <IconQuestionMark size={24} />
                    </ThemeIcon>

                    <Text ml={-10} fw={500} fz={18} c={Colors.PurplePrimary}>
                      Question {questions.length + 1}
                    </Text>
                  </Group>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      variant="subtle"
                      color="red"
                      onClick={discardCurrentQuestion}
                      disabled={
                        (currentQuestion.title.trim() === "" &&
                          currentQuestion.options.length === 0) ||
                        isSaving
                      }
                      leftSection={<IconTrash size={16} />}
                    >
                      Discard
                    </Button>
                  </motion.div>
                </Group>

                <TextInput
                  label="Question Title"
                  placeholder="Enter your question"
                  value={currentQuestion.title}
                  onChange={(e) => {
                    setCurrentQuestion((prev) => ({
                      ...prev,
                      title: e.target.value,
                    }));
                    setError(null);
                  }}
                  onKeyDown={handleQuestionTitleKeyPress}
                  ref={questionTitleRef}
                  required
                  error={error && !currentQuestion.title.trim() ? error : null}
                  spellCheck={false}
                  size="md"
                  styles={{
                    label: {
                      fontSize: 14,
                      fontWeight: 400,
                      marginLeft: 4,
                    },
                  }}
                />

                <Group mt="xs">
                  <Radio
                    label="Single Answer"
                    checked={currentQuestion.type === "single"}
                    onChange={() => handleTypeChange("single")}
                    color="purple"
                  />
                  <Radio
                    label="Multiple Answers"
                    checked={currentQuestion.type === "multiple"}
                    onChange={() => handleTypeChange("multiple")}
                    color="purple"
                  />
                </Group>

                <Title order={4} mt="lg" c={Colors.PurplePrimary}>
                  Options
                </Title>

                {currentQuestion.options.map((option, index) => (
                  <motion.div
                    key={option.value}
                    initial={{ x: -20, opacity: 0 }}
                    animate={{ x: 0, opacity: 1 }}
                    exit={{ x: 20, opacity: 0 }}
                    transition={{ duration: 0.3, delay: index * 0.1 }}
                  >
                    <Paper
                      p="md"
                      withBorder
                      style={{
                        borderColor: "var(--mantine-color-purple-3)",
                        borderWidth: 0.1,
                        background: currentQuestion.answer?.includes(
                          option.value
                        )
                          ? "var(--mantine-color-purple-0)"
                          : "white",
                      }}
                    >
                      <Group align="center">
                        {currentQuestion.type === "single" ? (
                          <Radio
                            checked={currentQuestion.answer?.includes(
                              option.value
                            )}
                            onChange={() => {
                              setCurrentQuestion((prev) => ({
                                ...prev,
                                answer: [option.value],
                              }));
                            }}
                            color="purple"
                          />
                        ) : (
                          <Checkbox
                            checked={currentQuestion.answer?.includes(
                              option.value
                            )}
                            onChange={(e) => {
                              const isAnswer = currentQuestion.answer?.includes(
                                option.value
                              );
                              setCurrentQuestion((prev) => ({
                                ...prev,
                                answer: isAnswer
                                  ? (prev.answer || []).filter(
                                      (v) => v !== option.value
                                    )
                                  : [...(prev.answer || []), option.value],
                              }));
                            }}
                            color="purple"
                          />
                        )}
                        <TextInput
                          style={{ flex: 1 }}
                          placeholder="Enter option text"
                          value={option.label}
                          onChange={(e) => {
                            updateOption(option.value, "label", e.target.value);
                            setError(null);
                          }}
                          onKeyDown={(e) =>
                            handleOptionKeyPress(e, option.value)
                          }
                          autoFocus={option.value === focusedOptionId}
                          ref={(el) => (optionRefs.current[option.value] = el)}
                          error={
                            error && !option.label.trim()
                              ? "Option cannot be empty"
                              : null
                          }
                          spellCheck={false}
                          variant=""
                        />
                        <motion.div
                          whileHover={{ scale: 1.1 }}
                          whileTap={{ scale: 0.9 }}
                        >
                          <ActionIcon
                            color="red"
                            variant="subtle"
                            onClick={() => removeOption(option.value)}
                            size="lg"
                          >
                            <IconTrash size={18} />
                          </ActionIcon>
                        </motion.div>
                      </Group>
                    </Paper>
                  </motion.div>
                ))}

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    disabled={isSaving}
                    w="100%"
                    leftSection={<IconPlus size={16} />}
                    color={Colors.PurplePrimary}
                    onClick={addOption}
                    mt="md"
                  >
                    Add Option
                  </Button>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Button
                    loading={isSaving}
                    w="100%"
                    onClick={saveQuestion}
                    disabled={!isQuestionValid()}
                    color="purple"
                  >
                    {isEditing ? "Save Changes" : "Save & Add Another"}
                  </Button>
                </motion.div>
                {error && (
                  <Text c="red" size="sm" mt="xs">
                    {error}
                  </Text>
                )}
              </Stack>
            </Paper>
          </motion.div>

          {questions.length > 0 && (
            <motion.div
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              <Paper
                p="xl"
                w={600}
                withBorder
                style={{
                  background:
                    "linear-gradient(to bottom right, var(--mantine-color-purple-0), white)",
                  borderColor: "var(--mantine-color-purple-3)",
                }}
              >
                <Group justify="space-between" mb="xl">
                  <Group>
                    <ThemeIcon
                      size={40}
                      radius="md"
                      color="purple"
                      variant="light"
                    >
                      <IconListCheck size={24} />
                    </ThemeIcon>
                    <div>
                      <Title
                        order={3}
                        style={{ color: "var(--mantine-color-purple-7)" }}
                      >
                        Saved Questions
                      </Title>
                      <Text size="sm" c="dimmed">
                        {questions.length} question
                        {questions.length !== 1 ? "s" : ""} saved
                      </Text>
                    </div>
                  </Group>
                  <motion.div
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    <Button
                      disabled={isSaving}
                      variant="light"
                      color="red"
                      onClick={() => {
                        console.log(questions);
                      }}
                    >
                      Clear All
                    </Button>
                  </motion.div>
                </Group>
                <Stack>
                  {isLoadingAssessments ? (
                    <>
                      <Skeleton height={24} width={500} />
                      <Skeleton height={24} width={500} />
                      <Skeleton height={24} width={500} />
                      <Skeleton height={24} width={500} />
                      <Skeleton height={24} width={500} />
                    </>
                  ) : (
                    <>
                      {questions.map((question, index) => (
                        <motion.div
                          key={question.title}
                          initial={{ x: -20, opacity: 0 }}
                          animate={{ x: 0, opacity: 1 }}
                          transition={{ duration: 0.3, delay: index * 0.1 }}
                        >
                          <Paper
                            p="xl"
                            withBorder
                            style={{
                              borderColor: "var(--mantine-color-purple-3)",
                              background: "white",
                            }}
                          >
                            <Group justify="space-between" mb="md">
                              <Group>
                                <ThemeIcon
                                  size={30}
                                  radius="md"
                                  color="purple"
                                  variant="light"
                                >
                                  <Text fw={500}>{index + 1}</Text>
                                </ThemeIcon>
                                <Text fw={500} c={Colors.PurplePrimary}>
                                  {getEllipsisFromString(question.title, 44)}
                                </Text>
                              </Group>
                              <Group>
                                <motion.div
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <Button
                                    disabled={isSaving}
                                    variant="subtle"
                                    color="blue"
                                    size="sm"
                                    onClick={() => editQuestion(index)}
                                    leftSection={<IconEdit size={16} />}
                                  >
                                    Edit
                                  </Button>
                                </motion.div>
                                <motion.div
                                  whileHover={{ scale: 1.1 }}
                                  whileTap={{ scale: 0.9 }}
                                >
                                  <Button
                                    disabled={isSaving}
                                    variant="subtle"
                                    color="red"
                                    size="sm"
                                    onClick={() =>
                                      handleDeleteQuestion(question.id)
                                    }
                                    leftSection={<IconTrash size={16} />}
                                  >
                                    Delete
                                  </Button>
                                </motion.div>
                              </Group>
                            </Group>
                            <Stack mt="md">
                              {question.options.map((option) => (
                                <Group key={option.value}>
                                  {question.type === "single" ? (
                                    <Radio
                                      checked={question.answer?.includes(
                                        option.value
                                      )}
                                      readOnly
                                      color="purple"
                                    />
                                  ) : (
                                    <Checkbox
                                      checked={question.answer?.includes(
                                        option.value
                                      )}
                                      readOnly
                                      color="purple"
                                    />
                                  )}
                                  <Text
                                    style={{
                                      color: question.answer?.includes(
                                        option.value
                                      )
                                        ? "var(--mantine-color-purple-7)"
                                        : "inherit",
                                      fontWeight: question.answer?.includes(
                                        option.value
                                      )
                                        ? 500
                                        : 400,
                                    }}
                                  >
                                    {getEllipsisFromString(option.label, 50)}
                                  </Text>
                                </Group>
                              ))}
                            </Stack>
                          </Paper>
                        </motion.div>
                      ))}
                    </>
                  )}
                </Stack>
              </Paper>
            </motion.div>
          )}
        </Box>

        <Modal
          opened={discardOpened}
          onClose={closeDiscard}
          centered
          title="Discard Question"
          size="sm"
        >
          <Stack>
            <Text>
              Are you sure you want to discard this question? This action cannot
              be undone.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={closeDiscard}>
                Cancel
              </Button>
              <Button color="red" onClick={handleDiscardConfirm}>
                Discard
              </Button>
            </Group>
          </Stack>
        </Modal>

        <Modal
          opened={deleteOpened}
          onClose={isDeletingQuestion ? () => {} : closeDelete}
          centered
          title="Delete Question"
          size="sm"
        >
          <Stack>
            <Text>
              Are you sure you want to delete this question? This action cannot
              be undone.
            </Text>
            <Group justify="flex-end" mt="md">
              <Button
                variant="subtle"
                onClick={closeDelete}
                disabled={isDeletingQuestion}
              >
                Cancel
              </Button>
              <Button
                color="red"
                onClick={ConfirmDeleteQuestion}
                loading={isDeletingQuestion}
              >
                Delete
              </Button>
            </Group>
          </Stack>
        </Modal>
      </motion.div>
    </Container>
  );
}
